import { type ReactNode } from 'react';
import { Activity } from 'lucide-react';
import type { DailyEarning } from '../types';
import { useChartData } from '../hooks/use-chart-data';
import { ChartBars } from './chart-bars';
import { ChartEmptyState } from './chart-empty-state';

type Props = { daily: DailyEarning[] };

const SWATCHES: { cls: string; label: string }[] = [
  { cls: 'bg-white/20', label: '<33' },
  { cls: 'bg-brand-red', label: '33–65' },
  { cls: 'bg-success', label: '66–98' },
  { cls: 'bg-warning', label: '≥99' },
];

function Header(): ReactNode {
  return (
    <div className="flex items-center gap-sm">
      <Activity size={14} className="text-text-tertiary/50" />
      <span className="text-[10px] font-medium uppercase tracking-wider text-text-tertiary/50">
        Actividad
      </span>
    </div>
  );
}

function Swatch({ cls, label }: { cls: string; label: string }): ReactNode {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`h-2 w-2 rounded-sm ${cls}`} />
      <span>{label}</span>
    </div>
  );
}

function Legend(): ReactNode {
  return (
    <div className="mt-md flex flex-wrap items-center gap-md text-[10px] tabular-nums text-text-tertiary/70">
      {SWATCHES.map((s) => (
        <Swatch key={s.label} cls={s.cls} label={s.label} />
      ))}
    </div>
  );
}

export function ActivityChart({ daily }: Props): ReactNode {
  const { ref, buckets, maxH, hasData } = useChartData(daily);
  return (
    <div className="rounded-xl bg-white/[0.02] p-base">
      <Header />
      <div ref={ref} className="mt-md">
        {hasData ? (
          <ChartBars buckets={buckets} maxH={maxH} />
        ) : (
          <ChartEmptyState />
        )}
      </div>
      {hasData && <Legend />}
    </div>
  );
}
